/**
 * Public surface of the language front-end.
 *
 * Everything above this layer — the editor, the graph compiler, the app store —
 * imports from here and never reaches into the tokenizer or parser directly.
 */
import type { Program } from './ast/nodes';
import type { Diagnostic } from './diagnostics/types';
import { sortDiagnostics } from './diagnostics/types';
import type { Token } from './tokenizer/tokens';
import type { ParseOptions } from './parser/parser';
import { parse } from './parser/parser';
import type { SemanticInfo } from './semantic/analyze';
import { analyze } from './semantic/analyze';

export * from './ast/nodes';
export * from './ast/source';
export { StableIdFactory, deriveId } from './ast/ids';
export * from './diagnostics/types';
export { tokenize, withoutComments, DEFAULT_TOKENIZE_LIMITS } from './tokenizer/tokenizer';
export type { TokenizeResult, TokenizeLimits } from './tokenizer/tokenizer';
export * from './tokenizer/tokens';
export { parse, DEFAULT_PARSE_OPTIONS } from './parser/parser';
export type { ParseResult, ParseOptions } from './parser/parser';
export { analyze } from './semantic/analyze';
export type { SemanticInfo, CallSite } from './semantic/analyze';
export { resolveMetadata, ICON_SLUGS, ANNOTATION_NAMES } from './parser/metadata';
export type { IconSlug } from './parser/metadata';
export { formatDocument, DEFAULT_FORMAT_OPTIONS } from './formatter/format';
export type { FormatOptions, FormatResult } from './formatter/format';

/** Everything the rest of the app needs from one pass over the source. */
export interface AnalysisResult {
  readonly source: string;
  readonly program: Program;
  readonly semantics: SemanticInfo;
  /** Parse and semantic diagnostics, merged and ordered. */
  readonly diagnostics: readonly Diagnostic[];
  readonly tokens: readonly Token[];
}

/**
 * Tokenize, parse and analyse in one call. This is the entry point the debounced
 * update pipeline (and the Web Worker) uses.
 */
export function analyzeSource(source: string, options?: Partial<ParseOptions>): AnalysisResult {
  const parsed = parse(source, options);
  const semantics = analyze(parsed.program);
  return {
    source,
    program: parsed.program,
    semantics,
    diagnostics: sortDiagnostics([...parsed.diagnostics, ...semantics.diagnostics]),
    tokens: parsed.tokens,
  };
}
